import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  Download,
  Filter,
  Edit,
  Trash2,
  ChevronRight,
  TrendingUp,
  Calendar,
  LayoutGrid,
  List,
  ArrowUpDown,
  AlertCircle,
  Gift
} from "lucide-react";
import { getPayrollEmployeesApi, runPayrollApi, getMonthlyBonusApi, deleteBonusApi } from "../../app/api";
import PayrollKPIs from "./components/PayrollKPIs";
import EditPayrollInfoModal from "./components/EditPayrollInfoModal";
import BonusModal from "./components/BonusModal";
import { toast } from "react-hot-toast";
import moment from "moment";

const formatCurrency = (amount) => {
  const value = parseFloat(amount);
  return isNaN(value) ? "0" : value.toLocaleString('en-IN');
};

const EmployeeSalaries = () => {
  const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, 'month').format("YYYY-MM")); // YYYY-MM
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [viewType, setViewType] = useState("list"); // grid or list
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [showExistsModal, setShowExistsModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [bonuses, setBonuses] = useState([]);
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [selectedBonusEmployee, setSelectedBonusEmployee] = useState(null);
  const [viewMode, setViewMode] = useState("salaries");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    fetchEmployees();
  }, [selectedMonth, refreshTrigger]);



  const Tooltip = ({ text, children }) => {
    return (
      <div className="relative group flex items-center justify-center">
        {children}

        <div className="
        absolute bottom-full mb-2
        px-2 py-1 text-xs font-medium
        text-white bg-black rounded-md
        whitespace-nowrap
        opacity-0 scale-95
        group-hover:opacity-100 group-hover:scale-100
        transition-all duration-100
        pointer-events-none
      ">
          {text}
        </div>
      </div>
    );
  };





  const handleRunPayroll = async () => {
    try {
      setIsRunning(true);
      const res = await runPayrollApi({ month: selectedMonth });
      if (res.success) {
        toast.success("Payroll processed successfully!");
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error("Error running payroll:", error);
      setShowExistsModal(true);
    } finally {
      setIsRunning(false);
    }
  };

  const handleDeleteBonus = async (bonusId) => {
    if (!window.confirm("Are you sure you want to delete this bonus?")) return;
    try {
      const res = await deleteBonusApi(bonusId);
      if (res.success) {
        toast.success("Bonus deleted successfully!");
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error(res.message || "Failed to delete bonus");
      }
    } catch (error) {
      console.error("Error deleting bonus:", error);
      toast.error("An error occurred while deleting");
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const [empRes, bonusRes] = await Promise.all([
        getPayrollEmployeesApi(selectedMonth),
        getMonthlyBonusApi(selectedMonth)
      ]);

      if (empRes.success) {
        setEmployees(empRes.data);
      } else {
        toast.error(empRes.message || "Failed to fetch employee salaries");
      }

      if (bonusRes.success) {
        setBonuses(bonusRes.data);
      }
    } catch (error) {
      console.error("Error fetching salaries/bonuses:", error);
      toast.error("An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEmployees = [...employees].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const filteredEmployees = sortedEmployees.filter(emp =>
    (emp.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.employee_id || "").toString().includes(searchTerm)
  );

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMonth, viewMode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#12082F] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#12082F] text-white p-4 sm:p-6 font-sans">
      <div className="max-w-[1600px] mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
              EMPLOYEE SALARIES <span className="text-white/70 font-normal">| Payroll</span>
            </h1>
            <p className="text-white/70 text-[11px] font-medium tracking-widest uppercase mt-0.5">
              Manage your team's compensation and banking info
            </p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              max={moment().subtract(1, 'month').format("YYYY-MM")}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:outline-none focus:border-purple-500/50 transition cursor-pointer"
            />
            {/* <button
              onClick={handleRunPayroll}
              disabled={isRunning}
              className="bg-gradient-to-tr from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white px-6 py-2 rounded-xl font-bold text-sm shadow-xl shadow-purple-500/20 transition transform active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center gap-2"
            >
              {isRunning ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <TrendingUp size={18} />}
              {isRunning ? "Processing..." : "Run Payroll"}
            </button> */}
          </div>
        </div>

        {/* SUMMARY STATS */}
        {/* <PayrollKPIs
          month={selectedMonth}
          refreshTrigger={refreshTrigger}
        /> */}

        {/* SEARCH AND FILTERS */}
        <div className="bg-white/5 border border-white/10 p-2 rounded-[22px] flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 group-focus-within:text-purple-400 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Search employees by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-[18px] pl-12 pr-4 py-3 text-sm text-white focus:outline-none focus:border-purple-500/30 focus:bg-white/[0.08] transition-all"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex gap-2">
              <button
                onClick={() => setViewType("list")}
                className={`p-3 rounded-[18px] transition ${viewType === "list" ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-white/5 text-white/70 hover:bg-white/10 border border-transparent'}`}
                title="List View"
              >
                <List size={20} />
              </button>
              <button
                onClick={() => setViewType("grid")}
                className={`p-3 rounded-[18px] transition ${viewType === "grid" ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-white/5 text-white/70 hover:bg-white/10 border border-transparent'}`}
                title="Grid View"
              >
                <LayoutGrid size={20} />
              </button>
            </div>
            <div className="w-px bg-white/10 mx-1 hidden sm:block" />
            <button
              onClick={() => setViewMode(viewMode === "salaries" ? "bonuses" : "salaries")}
              className={`flex items-center gap-2 px-6 border rounded-[18px] text-sm font-bold transition whitespace-nowrap min-w-fit ${viewMode === "bonuses" ? 'bg-purple-500 border-purple-400 text-white' : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/60'}`}
            >
              <Gift size={18} />
              {viewMode === "salaries" ? "View Bonus" : "View Salaries"}
            </button>
          </div>
        </div>

        {viewMode === "salaries" && (
          filteredEmployees.length > 0 ? (
            viewType === "list" ? (
              <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[1100px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('employee_id')}>
                        <div className="flex items-center gap-2">ID <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                      </th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('name')}>
                        <div className="flex items-center gap-2">Employee <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition" /></div>
                      </th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Bank Details</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Account Name</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Account No.</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider">IFSC / PAN</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Base Salary</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-right">Bonus</th>
                      <th className="p-4 text-xs font-bold text-white/70 uppercase tracking-wider text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {currentEmployees.map((emp) => (
                      <tr key={emp.employee_id} className="hover:bg-white/5 transition">
                        <td className="p-4 text-sm text-white/60">{emp.employee_id}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-[12px] font-bold">
                              {(emp.name || "U")[0]}
                            </div>
                            <p className="text-sm font-semibold text-white">{emp.name || "Unknown"}</p>
                          </div>
                        </td>
                        <td className="p-4 text-sm">
                          <p className="text-white font-medium">{emp.bank_name || <span className="text-white/50 italic text-[11px]">Not Set</span>}</p>
                        </td>
                        <td className="p-4 text-sm">
                          <p className="text-white font-medium">{emp.account_name || <span className="text-white/50 italic text-[11px]">Not Set</span>}</p>
                        </td>
                        <td className="p-4 text-sm">
                          <p className="text-white font-mono">{emp.account_number || <span className="text-white/50 italic text-[11px]">Not Set</span>}</p>
                        </td>
                        <td className="p-4 text-sm">
                          <div className="space-y-1">
                            <p className="text-[11px] text-white/70 leading-none">IFSC: <span className="text-white/80">{emp.ifsc_code || "N/A"}</span></p>
                            <p className="text-[11px] text-white/70 leading-none">PAN: <span className="text-white/80">{emp.pan_number || "N/A"}</span></p>
                          </div>
                        </td>
                        <td className="p-4 text-sm font-black text-right text-green-400">
                          ₹{formatCurrency(emp.salary)}
                        </td>
                        <td className="p-4 text-sm font-black text-right text-blue-400">
                          ₹{formatCurrency(bonuses.find(b => b.name === emp.name)?.monthly_amount || 0)}
                        </td>
                        {/* <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                setEditingEmployee(emp);
                                setIsEditModalOpen(true);
                              }}
                              className="p-2 hover:bg-yellow-600/10 rounded-lg bg-yellow-500/10 text-yellow-500 hover:text-yellow-400 transition"
                              title="Edit Payroll Details"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => {
                                const empBonus = bonuses.find(b => b.name === emp.name);
                                setSelectedBonusEmployee({
                                  ...emp,
                                  bonus_id: empBonus?.id,
                                  bonus_amount: empBonus?.monthly_amount
                                });
                                setBonusModalOpen(true);
                              }}
                              className={`p-2 rounded-lg transition ${bonuses.find(b => b.name === emp.name) ? 'text-blue-400 hover:bg-blue-400/10' : 'text-green-400 hover:bg-green-400/10'}`}
                              title={bonuses.find(b => b.name === emp.name) ? "Edit Bonus" : "Create Bonus"}
                            >
                              <Gift size={16} />
                            </button>
                            {bonuses.find(b => b.name === emp.name) && (
                              <button
                                onClick={() => handleDeleteBonus(bonuses.find(b => b.name === emp.name).id)}
                                className="p-2  bg-red-500/10 text-red-400  hover:bg-red-600/10 rounded-lg  hover:text-red-500 transition"
                                title="Delete Bonus"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td> */}

                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">

                            {/* EDIT */}
                            <Tooltip text="Edit Details">
                              <button
                                onClick={() => {
                                  setEditingEmployee(emp);
                                  setIsEditModalOpen(true);
                                }}
                                className="p-2 hover:bg-yellow-600/10 rounded-lg bg-yellow-500/10 text-yellow-500 hover:text-yellow-400 transition"
                              >
                                <Edit size={16} />
                              </button>
                            </Tooltip>

                            {/* BONUS */}
                            <Tooltip text={bonuses.find(b => b.name === emp.name) ? "Edit Bonus" : "Add Bonus"}>
                              <button
                                onClick={() => {
                                  const empBonus = bonuses.find(b => b.name === emp.name);
                                  setSelectedBonusEmployee({
                                    ...emp,
                                    bonus_id: empBonus?.id,
                                    bonus_amount: empBonus?.monthly_amount
                                  });
                                  setBonusModalOpen(true);
                                }}
                                className={`p-2 rounded-lg transition ${bonuses.find(b => b.name === emp.name)
                                  ? 'text-blue-400 hover:bg-blue-400/10'
                                  : 'text-green-400 hover:bg-green-400/10'
                                  }`}
                              >
                                <Gift size={16} />
                              </button>
                            </Tooltip>

                            {/* DELETE */}
                            {bonuses.find(b => b.name === emp.name) && (
                              <Tooltip text="Delete Bonus">
                                <button
                                  onClick={() => handleDeleteBonus(bonuses.find(b => b.name === emp.name).id)}
                                  className="p-2 bg-red-500/10 text-red-400 hover:bg-red-600/10 rounded-lg hover:text-red-500 transition"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </Tooltip>
                            )}

                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {currentEmployees.map((emp) => (
                  <div key={emp.employee_id} className="bg-white/5 border border-white/10 p-5 rounded-2xl hover:border-purple-500/50 transition space-y-4 group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center font-bold border-2 border-white/10">
                          {(emp.name || "U")[0]}
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-white group-hover:text-purple-400 transition">{emp.name || "Unknown"}</h4>
                          <p className="text-[10px] text-white/70">ID: {emp.employee_id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 pt-2 border-t border-white/5">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">Bank</span>
                        <span className="text-xs font-medium text-white/80">{emp.bank_name || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">A/C Name</span>
                        <span className="text-xs font-medium text-white/80">{emp.account_name || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">Account</span>
                        <span className="text-xs font-mono text-white/80">{emp.account_number || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-white/5 pt-2">
                        <span className="text-[10px] text-white/60 uppercase font-black">Base Salary</span>
                        <span className="text-sm font-black text-green-400">₹{formatCurrency(emp.salary)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-white/60 uppercase font-black">Monthly Bonus</span>
                        <span className="text-sm font-black text-blue-400">₹{formatCurrency(bonuses.find(b => b.name === emp.name)?.monthly_amount || 0)}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingEmployee(emp);
                          setIsEditModalOpen(true);
                        }}
                        className="flex-1 py-2 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/20 rounded-xl transition text-[10px] font-bold flex items-center justify-center gap-2 text-yellow-400"
                      >
                        <Edit size={14} /> Edit Info
                      </button>
                      <button
                        onClick={() => {
                          const empBonus = bonuses.find(b => b.name === emp.name);
                          setSelectedBonusEmployee({
                            ...emp,
                            bonus_id: empBonus?.id,
                            bonus_amount: empBonus?.monthly_amount
                          });
                          setBonusModalOpen(true);
                        }}
                        className={`flex-1 py-2 border rounded-xl transition text-[10px] font-bold flex items-center justify-center gap-2 ${bonuses.find(b => b.name === emp.name) ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' : 'bg-green-500/10 border-green-500/20 text-green-400'}`}
                        title={bonuses.find(b => b.name === emp.name) ? "Edit Bonus" : "Create Bonus"}
                      >
                        <Gift size={14} /> {bonuses.find(b => b.name === emp.name) ? "Edit Bonus" : "Create Bonus"}
                      </button>
                      {bonuses.find(b => b.name === emp.name) && (
                        <button
                          onClick={() => handleDeleteBonus(bonuses.find(b => b.name === emp.name).id)}
                          className="p-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl hover:bg-red-500/20 transition"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
              <div className="bg-white/5 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={32} className="text-white/50" />
              </div>
              <h3 className="text-lg font-bold text-white">No employees found</h3>
              <p className="text-white/70 text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          )
        )}

        {/* PAGINATION FOR SALARIES */}
        {viewMode === "salaries" && filteredEmployees.length > itemsPerPage && (
          <div className="flex items-center justify-center gap-2 py-6">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} className="rotate-180" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded-lg text-sm font-bold transition ${currentPage === page
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 text-white/70 hover:text-white transition disabled:opacity-30"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}

        {viewMode === "bonuses" && (
          /* BONUS LIST VIEW */
          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-xl">
              <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider">Employee Name</th>
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider">Salary ID</th>
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider text-center">Month</th>
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider text-right">Bonus Amount</th>
                    <th className="p-4 text-[10px] uppercase font-black text-white/70 tracking-wider text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {bonuses.length > 0 ? bonuses.map((bonus) => (
                    <tr key={bonus.id} className="hover:bg-white/[0.02] transition">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold">
                            {(bonus.name || "U")[0]}
                          </div>
                          <p className="text-sm font-semibold text-white">{bonus.name}</p>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-white/70 font-mono">{bonus.employee_salary_id}</td>
                      <td className="p-4 text-sm text-white/60 text-center uppercase font-bold">{bonus.month}</td>
                      <td className="p-4 text-sm font-black text-right text-blue-400">₹{formatCurrency(bonus.monthly_amount)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {
                              const emp = employees.find(e => e.name === bonus.name);
                              setSelectedBonusEmployee({
                                ...emp,
                                bonus_id: bonus.id,
                                bonus_amount: bonus.monthly_amount,
                                name: bonus.name,
                                employee_id: bonus.employee_salary_id
                              });
                              setBonusModalOpen(true);
                            }}
                            className="p-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition"
                            title="Edit Bonus"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteBonus(bonus.id)}
                            className="p-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition"
                            title="Delete Bonus"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="p-12 text-center text-white/70">
                        <Gift size={48} className="mx-auto mb-4 opacity-10" />
                        No bonuses recorded for this month
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ALREADY EXISTS MODAL */}
      {showExistsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowExistsModal(false)} />
          <div className="relative bg-[#1B103F] border border-white/10 w-full max-w-md rounded-3xl p-8 shadow-2xl space-y-6 text-center transform transition-all">
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle size={40} className="text-yellow-500" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-white">Payroll Already Processed</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Payroll for <span className="text-white font-bold">{new Date(selectedMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}</span> has already been generated and processed.
              </p>
            </div>
            <div className="flex flex-col gap-3 pt-4">
              <button
                onClick={() => setShowExistsModal(false)}
                className="w-full bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold transition border border-white/10"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <EditPayrollInfoModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        employee={editingEmployee}
        onUpdate={() => setRefreshTrigger(prev => prev + 1)}
      />

      <BonusModal
        isOpen={bonusModalOpen}
        onClose={() => setBonusModalOpen(false)}
        employee={selectedBonusEmployee}
        selectedMonth={selectedMonth}
        onUpdate={() => setRefreshTrigger(prev => prev + 1)}
      />
    </div>
  );
};

export default EmployeeSalaries;
